import * as Sentry from '@sentry/react';
import type { QueryObserverResult } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect, useMemo } from 'react';
import { useInitiativeAvailableTargets } from '../../../../api/initiative-client/initiative-client.hooks';
import { TargetComparison, type GetAvailableTargetsResponse } from '../../../../api/targets-client/targets-client.type';
import { useAvailableMeasures } from '../../../../api/work-periods-client/work-periods-client.hooks';
import {
  ModuleType,
  TimeAllocationType,
  type AvailableMeasuresResponse,
  type MeasureDataResponse,
  type MeasureMetadata,
} from '../../../../api/work-periods-client/work-periods-client.type';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import { useStrategyStore, useTargetsTabStore } from '../../../../store/strategy-store/strategy-store';
import {
  setAvailableMeasures,
  setMeasureData,
  setSelectedTarget,
  setTargetsData,
} from '../../../../store/strategy-store/strategy-store.actions';
import { mergeMeasureMetaDataAndTargets } from '../../../process-analysis/measure-comparison/measure-comparison.helpers';
import { useInitiativeMeasureData } from '../../../strategy/initiative-performance/section-targets/initiative-targets.hooks';
import { MeasureDataType, type ChartDataItem } from './targets-tab.type';

/**
 * Custom hook that fetches the targets data
 *
 * @returns {Object} Object containing loading state
 * @returns {boolean} isLoading - Whether any of the data sources are still loading
 */
const useTargetsData = (): { isLoading: boolean } => {
  const { query: availableTargetsQuery } = useAvailableTargetsData();
  const { query: availableMeasuresQuery } = useAvailableMeasuresData();
  const { isLoading: measureDataLoading } = useMeasureData();

  return { isLoading: availableTargetsQuery.isLoading || availableMeasuresQuery.isLoading || measureDataLoading };
};

/**
 * Custom hook that fetches available targets data for an initiative
 *
 * @returns {Object} Object containing the query result
 * @returns {QueryObserverResult<GetAvailableTargetsResponse>} query - The query result object
 */
const useAvailableTargetsData = (): {
  query: QueryObserverResult<GetAvailableTargetsResponse>;
} => {
  const organizationId = useGlobalStore((state) => state.organization?.id);
  const initiative = useStrategyStore((state) => state.initiativePerformance);

  const payload = {
    organization_id: organizationId || '',
    initiative_ids: initiative?.id,
    start_date: initiative?.start_date ? dayjs(initiative.start_date).format('YYYY-MM-DD') : '',
    end_date: initiative?.end_date ? dayjs(initiative.end_date).format('YYYY-MM-DD') : '',
    time_allocation_type: TimeAllocationType.BiWeekly,
  };

  const options = {
    enabled: !!organizationId && !!initiative?.id,
  };

  const { data, query } = useInitiativeAvailableTargets(payload, options);

  useEffect(() => {
    if (data && initiative?.id) {
      const targets = data[initiative.id];

      setTargetsData(targets);
      setSelectedTarget(targets.length > 0 ? targets[0] : null);
    }
  }, [data, initiative?.id]);

  useEffect(() => {
    if (query.error) {
      setTargetsData([]);
      Sentry.captureException(query.error);
    }
  }, [query.error]);

  return { query };
};

/**
 * Custom hook that fetches available measures data for an initiative
 *
 * @returns {Object} Object containing the query result
 * @returns {QueryObserverResult<AvailableMeasuresResponse>} query - The query result object
 */
const useAvailableMeasuresData = (): {
  query: QueryObserverResult<AvailableMeasuresResponse>;
} => {
  const initiative = useStrategyStore((state) => state.initiativePerformance);

  const { data, query } = useAvailableMeasures({
    payload: {
      scope: [ModuleType.ProcessAnalysis],
      project_id: initiative?.projects?.[0]?.id || undefined,
    },
    options: {
      enabled: !!initiative?.projects?.[0]?.id,
      staleTime: Infinity,
    },
  });

  useEffect(() => {
    if (data) {
      setAvailableMeasures(data[ModuleType.ProcessAnalysis].measures || []);
    }
  }, [data]);

  useEffect(() => {
    if (query.error) {
      setAvailableMeasures([]);
      Sentry.captureException(query.error);
    }
  }, [query.error]);

  return { query };
};

/**
 * Custom hook that merges available measures with targets data
 *
 * @returns {MeasureMetadata[]} Array of measure metadata objects merged with targets
 */
const useMergedTargets = (): MeasureMetadata[] => {
  const availableMeasures = useTargetsTabStore((state) => state.availableMeasures);
  const targets = useTargetsTabStore((state) => state.targets);

  const mergedTargets = useMemo(
    () => mergeMeasureMetaDataAndTargets(availableMeasures || [], targets || [], true),
    [availableMeasures, targets],
  );

  return mergedTargets;
};

/**
 * Custom hook that fetches measure data for an initiative
 *
 * @returns {Object} Object containing the query result
 * @returns {boolean} isLoading - Whether any of the data sources are still loading
 */
const useMeasureData = (): { isLoading: boolean } => {
  const initiative = useStrategyStore((state) => state.initiativePerformance);
  const measureMetadata = useMergedTargets();

  const { data, isLoading } = useInitiativeMeasureData({ initiative, measureMetadata });

  useEffect(() => {
    if (data) {
      const measureData = data.filter((measure) => measure !== undefined);
      const measureDataObject = measureData.reduce((acc, item) => {
        return {
          ...acc,
          ...item,
        };
      }, {} as MeasureDataResponse);

      setMeasureData(measureDataObject);
    }
  }, [data]);

  return { isLoading };
};

/**
 * Custom hook that generates chart data for the targets tab
 *
 * @returns {Object} Object containing the chart data
 */
const useTargetChartData = () => {
  const selectedTarget = useTargetsTabStore((state) => state.selectedTarget);
  const measureData = useTargetsTabStore((state) => state.measureData);

  return useMemo(() => {
    if (!selectedTarget || !measureData) {
      return {
        chartData: [],
      };
    }

    const measureDataForTarget = measureData[selectedTarget.measure as keyof MeasureDataResponse];

    if (!measureDataForTarget) {
      return {
        chartData: [],
      };
    }

    const isRange = [TargetComparison.RANGE_EX, TargetComparison.RANGE_IN].includes(selectedTarget.target_comparison);

    const chartData: ChartDataItem[] = Object.entries(measureDataForTarget).map(([date, value]) => {
      const dataPoint = selectedTarget.time_allocations[date] ?? null;

      const targetValue =
        isRange && typeof dataPoint === 'object' && dataPoint !== null
          ? [dataPoint.value_lower, dataPoint.value_upper]
          : (dataPoint as number);

      return {
        date,
        [MeasureDataType.Actual]: value,
        [MeasureDataType.Target]: targetValue,
      };
    });

    chartData.sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf());

    return {
      chartData,
    };
  }, [selectedTarget, measureData]);
};

export { useTargetChartData, useTargetsData };
